/**
 * Snelheid (LCP op mobiel): beelden die bij het openen buiten beeld staan
 * (thumbnails 7+, UGC-video-posters, stapfoto's) wachten tot de eerste
 * galerijfoto van de productpagina geladen is. Anders concurreren ze met die
 * foto om de bandbreedte.
 *
 * snippets/oppie-hold-image.liquid zet src/srcset in data-oppie-hold-src /
 * data-oppie-hold-srcset. Dit script zet ze terug:
 *  - meteen, voor een beeld dat al in beeld staat (IntersectionObserver, dus
 *    ook rekening houdend met overflow-clipping van de thumbnail-strook);
 *  - voor de rest zodra de eerste galerijfoto geladen is (of bij window load,
 *    of meteen als er geen galerij op de pagina staat).
 * Daarna laadt de browser ze lazy zoals voorheen (loading="lazy" blijft staan).
 * Later toegevoegde of gemorphte beelden (variantwissel, quick add) worden
 * na dat moment direct teruggezet.
 */

const SRC_ATTR = 'data-oppie-hold-src';
const SRCSET_ATTR = 'data-oppie-hold-srcset';
const HELD_SELECTOR = `img[${SRC_ATTR}], img[${SRCSET_ATTR}]`;
const HERO_SELECTOR = 'oppie-gallery [data-oppie-main] .swiper-slide img';

let heroReady = false;
let viewportObserver = null;

function releaseImage(img) {
  try {
    const srcset = img.getAttribute(SRCSET_ATTR);
    const src = img.getAttribute(SRC_ATTR);
    if (srcset !== null) {
      img.setAttribute('srcset', srcset);
      img.removeAttribute(SRCSET_ATTR);
    }
    if (src !== null) {
      img.setAttribute('src', src);
      img.removeAttribute(SRC_ATTR);
    }
    viewportObserver?.unobserve(img);
  } catch (error) {
    console.error('[oppie-hold-images] release failed', error);
  }
}

function heldImagesIn(root) {
  if (!root || root.nodeType !== Node.ELEMENT_NODE && root !== document) return [];
  const found = root.querySelectorAll ? Array.from(root.querySelectorAll(HELD_SELECTOR)) : [];
  if (root.matches?.(HELD_SELECTOR)) found.push(root);
  return found;
}

function releaseAll(root = document) {
  heldImagesIn(root).forEach(releaseImage);
}

function whenHeroLoaded() {
  return new Promise((resolve) => {
    if (document.readyState === 'complete') return resolve();
    window.addEventListener('load', () => resolve(), { once: true });

    const hero = document.querySelector(HERO_SELECTOR);
    if (!hero || hero.complete) return resolve();
    hero.addEventListener('load', () => resolve(), { once: true });
    hero.addEventListener('error', () => resolve(), { once: true });
  });
}

function observeVisible() {
  if (!('IntersectionObserver' in window)) return;
  viewportObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) releaseImage(entry.target);
    });
  });
  heldImagesIn(document).forEach((img) => viewportObserver.observe(img));
}

function watchLateImages() {
  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'attributes') {
        if (heroReady) releaseImage(record.target);
        else viewportObserver?.observe(record.target);
        return;
      }
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (heroReady) releaseAll(node);
        else heldImagesIn(node).forEach((img) => viewportObserver?.observe(img));
      });
    });
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [SRC_ATTR, SRCSET_ATTR],
  });
}

try {
  observeVisible();
  watchLateImages();
  whenHeroLoaded().then(() => {
    heroReady = true;
    viewportObserver?.disconnect();
    viewportObserver = null;
    releaseAll();
  });
} catch (error) {
  console.error('[oppie-hold-images] init failed; releasing all images', error);
  heroReady = true;
  releaseAll();
}
